import { useState, useEffect, useCallback } from 'react';

import { supabase } from '../lib/supabase';
import { unwrap } from '../lib/supabaseUnwrap';
import { dodajKwote, KwotyWgWalut } from '../lib/waluty';

// Polish voivodeships (województwa) for partner regions
export const POLISH_REGIONS = [
  { code: 'DS', name: 'dolnośląskie' },
  { code: 'KP', name: 'kujawsko-pomorskie' },
  { code: 'LU', name: 'lubelskie' },
  { code: 'LB', name: 'lubuskie' },
  { code: 'LD', name: 'łódzkie' },
  { code: 'MA', name: 'małopolskie' },
  { code: 'MZ', name: 'mazowieckie' },
  { code: 'OP', name: 'opolskie' },
  { code: 'PK', name: 'podkarpackie' },
  { code: 'PD', name: 'podlaskie' },
  { code: 'PM', name: 'pomorskie' },
  { code: 'SL', name: 'śląskie' },
  { code: 'SK', name: 'świętokrzyskie' },
  { code: 'WN', name: 'warmińsko-mazurskie' },
  { code: 'WP', name: 'wielkopolskie' },
  { code: 'ZP', name: 'zachodniopomorskie' },
];

export interface Partner {
  id: string;
  userId: string;
  organizationId: string | null;
  companyName: string;
  contactName: string;
  email: string;
  phone: string | null;
  address: string | null;
  city: string | null;
  postalCode: string | null;
  county: string | null;
  voivodeship: string | null;
  taxId: string | null;
  regon: string | null;
  krs: string | null;
  vatStatus: 'active' | 'exempt' | 'not_registered' | null;
  status: 'active' | 'pending' | 'suspended' | 'inactive';
  regions: string[]; // voivodeship codes
  commissionRate: number;
  /** Próg programu partnerskiego (partner_progi.klucz), np. 'brazowy'…'platynowy'. */
  progProwizji: string | null;
  /** true = próg zamrożony ręcznie (umowa indywidualna), przeliczanie go nie zmienia. */
  progReczny: boolean;
  totalClients: number;
  activeClients: number;
  /** Sumy prowizji per waluta — wiersze partner_commissions bywają w EUR i PLN. */
  totalCommission: KwotyWgWalut;
  pendingCommission: KwotyWgWalut;
  createdAt: string;
}

export interface PartnersFilter {
  search?: string;
  status?: string;
  region?: string;
}

export interface CreatePartnerInput {
  userId?: string;
  email: string;
  companyName: string;
  contactName: string;
  phone?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  county?: string;
  voivodeship?: string;
  taxId?: string;
  regon?: string;
  krs?: string;
  vatStatus?: 'active' | 'exempt' | 'not_registered';
  vatApiResponse?: Record<string, unknown>;
  regions: string[];
  commissionRate?: number;
}

export interface UpdatePartnerInput {
  companyName?: string;
  contactName?: string;
  email?: string;
  phone?: string;
  status?: 'active' | 'pending' | 'suspended' | 'inactive';
  regions?: string[];
  commissionRate?: number;
}

/** Raw snake_case row shape of the `partners` table (columns used by this hook). */
interface DbPartnerRow {
  id: string;
  user_id: string;
  organization_id: string | null;
  company_name: string;
  contact_name: string;
  email: string;
  phone: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  county: string | null;
  voivodeship: string | null;
  tax_id: string | null;
  regon: string | null;
  krs: string | null;
  vat_status: Partner['vatStatus'];
  status: Partner['status'] | null;
  regions: string[] | null;
  commission_rate: number | null;
  prog_prowizji: string | null;
  prog_reczny: boolean | null;
  created_at: string;
}

interface PartnerAggregates {
  totalClients: number;
  activeClients: number;
  totalCommission: KwotyWgWalut;
  pendingCommission: KwotyWgWalut;
}

// Funkcja, nie stała: zagnieżdżone słowniki kwot muszą być świeże dla każdego
// partnera — płytka kopia stałej współdzieliłaby jeden obiekt sum między wszystkich.
const emptyAggregates = (): PartnerAggregates => ({
  totalClients: 0,
  activeClients: 0,
  totalCommission: {},
  pendingCommission: {},
});

/** Map a raw snake_case `partners` row to the camelCase `Partner` type. */
function mapDbPartner(row: DbPartnerRow, stats: PartnerAggregates = emptyAggregates()): Partner {
  return {
    id: row.id,
    userId: row.user_id,
    organizationId: row.organization_id || null,
    companyName: row.company_name,
    contactName: row.contact_name,
    email: row.email,
    phone: row.phone,
    address: row.address,
    city: row.city,
    postalCode: row.postal_code,
    county: row.county,
    voivodeship: row.voivodeship,
    taxId: row.tax_id,
    regon: row.regon,
    krs: row.krs,
    vatStatus: row.vat_status,
    status: row.status || 'active',
    regions: row.regions || [],
    commissionRate: row.commission_rate || 20,
    progProwizji: row.prog_prowizji || null,
    progReczny: row.prog_reczny === true,
    totalClients: stats.totalClients,
    activeClients: stats.activeClients,
    totalCommission: stats.totalCommission,
    pendingCommission: stats.pendingCommission,
    createdAt: row.created_at,
  };
}

export function usePartners(initialFilters?: PartnersFilter) {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [filters, setFilters] = useState<PartnersFilter>(initialFilters || {});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  const fetchPartners = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      let query = supabase.from('partners').select(
        `
          id,
          user_id,
          organization_id,
          company_name,
          contact_name,
          email,
          phone,
          address,
          city,
          postal_code,
          county,
          voivodeship,
          tax_id,
          regon,
          krs,
          vat_status,
          status,
          regions,
          commission_rate,
          prog_prowizji,
          prog_reczny,
          created_at
        `,
        { count: 'exact' }
      );

      // Apply filters
      if (filters.search) {
        query = query.or(
          `company_name.ilike.%${filters.search}%,contact_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`
        );
      }
      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (filters.region) {
        query = query.contains('regions', [filters.region]);
      }

      query = query.order('created_at', { ascending: false });

      const { data, error: queryError, count } = await query;

      if (queryError) throw new Error(queryError.message);

      const rows: DbPartnerRow[] = data || [];

      // organizations.partner_id references the partner's ORGANIZATION
      // (organizations.id) — client counts are keyed by that, unchanged.
      // partner_commissions.partner_id is DIFFERENT: since migration 465 it
      // references partners.id directly (repo used to say otherwise; that
      // was the bug — production's FK never matched that comment). The two
      // stats therefore need two separate keys, not one shared org-id map.
      const partnerOrgIds = Array.from(new Set(rows.map((p) => p.organization_id || p.id)));
      const partnerIds = rows.map((p) => p.id);

      const clientStatsByOrgId = new Map<string, PartnerAggregates>();
      const commissionStatsByPartnerId = new Map<string, PartnerAggregates>();
      const getAggregates = (
        map: Map<string, PartnerAggregates>,
        key: string
      ): PartnerAggregates => {
        let agg = map.get(key);
        if (!agg) {
          agg = emptyAggregates();
          map.set(key, agg);
        }
        return agg;
      };

      if (partnerIds.length > 0) {
        const [orgsRes, commissionsRes] = await Promise.all([
          partnerOrgIds.length > 0
            ? supabase
                .from('organizations')
                .select('partner_id, status')
                .in('partner_id', partnerOrgIds)
            : Promise.resolve({ data: [], error: null }),
          supabase
            .from('partner_commissions')
            .select('partner_id, amount, currency, status')
            .in('partner_id', partnerIds),
        ]);

        unwrap(orgsRes).forEach((org: { partner_id: string | null; status: string | null }) => {
          if (!org.partner_id) return;
          const agg = getAggregates(clientStatsByOrgId, org.partner_id);
          agg.totalClients += 1;
          if (org.status === 'active') agg.activeClients += 1;
        });

        unwrap(commissionsRes).forEach(
          (c: {
            partner_id: string;
            amount: number | null;
            currency: string | null;
            status: string | null;
          }) => {
            const agg = getAggregates(commissionStatsByPartnerId, c.partner_id);
            dodajKwote(agg.totalCommission, c.currency, c.amount || 0);
            if (c.status === 'pending')
              dodajKwote(agg.pendingCommission, c.currency, c.amount || 0);
          }
        );
      }

      setPartners(
        rows.map((p) => {
          const clientStats = clientStatsByOrgId.get(p.organization_id || p.id);
          const commissionStats = commissionStatsByPartnerId.get(p.id);
          return mapDbPartner(p, {
            totalClients: clientStats?.totalClients ?? 0,
            activeClients: clientStats?.activeClients ?? 0,
            totalCommission: commissionStats?.totalCommission ?? {},
            pendingCommission: commissionStats?.pendingCommission ?? {},
          });
        })
      );
      setTotal(count || 0);
    } catch (err) {
      console.error('Error fetching partners:', err);
      setError(err instanceof Error ? err.message : 'Nie udało się załadować listy partnerów');
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const createPartner = async (input: CreatePartnerInput): Promise<Partner | null> => {
    try {
      // Create the partner record
      const { data, error } = await supabase
        .from('partners')
        .insert({
          user_id: input.userId || null,
          email: input.email,
          company_name: input.companyName,
          contact_name: input.contactName,
          phone: input.phone || null,
          address: input.address || null,
          city: input.city || null,
          postal_code: input.postalCode || null,
          county: input.county || null,
          voivodeship: input.voivodeship || null,
          tax_id: input.taxId || null,
          regon: input.regon || null,
          krs: input.krs || null,
          vat_status: input.vatStatus || null,
          vat_api_response: input.vatApiResponse || null,
          vat_data_fetched_at: input.vatApiResponse ? new Date().toISOString() : null,
          regions: input.regions,
          commission_rate: input.commissionRate || 20,
          status: 'pending',
        })
        .select()
        .single();

      if (error) throw error;

      // Send invitation email to the partner
      // This will create the auth user and send an email
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData?.session?.access_token) {
          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL || ''}/functions/v1/admin-user-actions`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${sessionData.session.access_token}`,
              },
              body: JSON.stringify({
                action: 'invite_partner',
                email: input.email,
                partner_id: data.id,
                company_name: input.companyName,
                contact_name: input.contactName,
              }),
            }
          );

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.warn('Failed to send partner invitation email:', errorData);
            // Don't throw - partner was created, email just failed
          } else {
            console.log('Partner invitation email sent successfully');
          }
        }
      } catch (emailErr) {
        console.warn('Error sending partner invitation email:', emailErr);
        // Don't throw - partner was created, email just failed
      }

      await fetchPartners();
      // The insert returns a raw snake_case row — map it to the Partner shape.
      return mapDbPartner(data as DbPartnerRow);
    } catch (err) {
      console.error('Error creating partner:', err);
      throw new Error('Nie udało się utworzyć partnera');
    }
  };

  const updatePartner = async (id: string, input: UpdatePartnerInput): Promise<Partner | null> => {
    try {
      // First, get current partner data to check if we're activating
      const { data: currentPartner, error: fetchError } = await supabase
        .from('partners')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (fetchError) throw fetchError;
      if (!currentPartner) return null;

      const updateData: Record<string, unknown> = {};
      if (input.companyName !== undefined) updateData.company_name = input.companyName;
      if (input.contactName !== undefined) updateData.contact_name = input.contactName;
      if (input.email !== undefined) updateData.email = input.email;
      if (input.phone !== undefined) updateData.phone = input.phone;
      if (input.status !== undefined) updateData.status = input.status;
      if (input.regions !== undefined) updateData.regions = input.regions;
      if (input.commissionRate !== undefined) updateData.commission_rate = input.commissionRate;

      // Check if partner needs an organization created
      const isActivating = input.status === 'active' && currentPartner.status === 'pending';
      const isAlreadyActiveWithoutOrg =
        currentPartner.status === 'active' && !currentPartner.organization_id;
      const hasNoOrganization = !currentPartner.organization_id;

      // Create organization if activating a pending partner OR if already active but missing org.
      // Routed through the atomic SECURITY DEFINER RPC, which creates the organization +
      // 'partner' membership, links partners.organization_id, and writes a partner.approve
      // audit_log entry. This replaces the previous direct organizations/user_organizations
      // inserts, which bypassed the audit trail and duplicated the RPC's logic.
      if ((isActivating || isAlreadyActiveWithoutOrg) && hasNoOrganization) {
        const { error: activateError } = await supabase.rpc('superadmin_activate_partner_org', {
          p_partner_id: id,
        });

        if (activateError) {
          console.error('Error activating partner organization:', activateError);
          throw new Error('Nie udało się utworzyć organizacji dla partnera');
        }
        // organization_id is set on the partners row by the RPC; do not set it client-side.
      }

      const { data, error } = await supabase
        .from('partners')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      await fetchPartners();
      // The update returns a raw snake_case row — map it to the Partner shape.
      return mapDbPartner(data as DbPartnerRow);
    } catch (err) {
      console.error('Error updating partner:', err);
      throw new Error('Nie udało się zaktualizować partnera');
    }
  };

  const deletePartner = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('partners').delete().eq('id', id);

      if (error) throw error;

      await fetchPartners();
      return true;
    } catch (err) {
      console.error('Error deleting partner:', err);
      throw new Error('Nie udało się usunąć partnera');
    }
  };

  const activatePartner = async (id: string): Promise<boolean> => {
    await updatePartner(id, { status: 'active' });
    return true;
  };

  const setupPartnerOrganization = async (id: string): Promise<boolean> => {
    // Trigger updatePartner with current status - this will detect missing org and create it
    const partner = partners.find((p) => p.id === id);
    if (!partner) throw new Error('Nie znaleziono partnera');
    await updatePartner(id, { status: partner.status });
    return true;
  };

  const suspendPartner = async (id: string): Promise<boolean> => {
    await updatePartner(id, { status: 'suspended' });
    return true;
  };

  const assignRegions = async (id: string, regions: string[]): Promise<boolean> => {
    await updatePartner(id, { regions });
    return true;
  };

  const getRegionName = (code: string): string => {
    const region = POLISH_REGIONS.find((r) => r.code === code);
    return region ? region.name : code;
  };

  return {
    partners,
    total,
    isLoading,
    error,
    filters,
    setFilters,
    refetch: fetchPartners,
    createPartner,
    updatePartner,
    deletePartner,
    activatePartner,
    setupPartnerOrganization,
    suspendPartner,
    assignRegions,
    getRegionName,
    POLISH_REGIONS,
  };
}
